/**
 * installErrorCapture — auto-file browser errors as issues (ADR-0121).
 *
 * Behavioural reference: QuantFun's `ui/src/lib/qa.ts` (the ideas, ported;
 * not the code). Installed ONCE at boot from `main.tsx`, next to
 * `install401Handler()`. It:
 *
 *   - overrides `console.error` / `console.warn` to keep a 20-line ring
 *     buffer (300 chars/line) that ALWAYS passes through to the original
 *     with identical arguments (never swallows a log);
 *   - listens for window `error` and `unhandledrejection` and files a
 *     `kind:'auto'` report carrying title, stack, route, url params, user
 *     agent, and the console tail;
 *   - dedups per tab session (an in-memory Set keyed on normalized message
 *     + route) so one crash files at most once per session — the server
 *     dedups across sessions;
 *   - exports `reportedErrors` (a WeakSet) so the React error boundary can
 *     mark an Error it already reported and this window listener skips it —
 *     one crash, one report.
 *
 * Two hard guarantees (both are tested):
 *   1. The ENTIRE install body is wrapped so a synchronous throw during
 *      install never propagates — the app must still boot even if telemetry
 *      install fails.
 *   2. The reporter is silent and non-amplifying: it never logs, never
 *      throws into the app, and an error thrown INSIDE the reporter can
 *      never re-enter capture (which would storm).
 *
 * Scope (ADR-0121 v1): auto reports fire only for authenticated,
 * tenant-pinned pages. The install runs at boot (before auth resolves),
 * so the REPORTING call — not the install — is gated: pre-auth routes
 * (`/auth/*` and the `/` redirect) drop their errors silently.
 *
 * No-login pages (`/l/<secret>`, secure links; `/f/<secret>`, Forms) never
 * report either: there the page address IS the secret, and a report would
 * store it in the issues table (secure links SL28).
 */

import { createIssue } from "./issuesApi";

// ---------------------------------------------------------------------------
// Module state (per tab session).
// ---------------------------------------------------------------------------

const TAIL_MAX_LINES = 20;
const TAIL_MAX_LINE_CHARS = 300;
const TITLE_MAX_CHARS = 300;
const NORM_MSG_MAX_CHARS = 200;

const tail: string[] = [];
const seenHashes = new Set<string>();

/**
 * Errors already reported by the React error boundary. The window `error`
 * listener skips any Error present here so a boundary-caught crash that
 * React also re-throws to `window.onerror` is reported exactly once.
 */
export const reportedErrors = new WeakSet<object>();

let installed = false;

// ---------------------------------------------------------------------------
// Console ring buffer.
// ---------------------------------------------------------------------------

function safeStringify(value: unknown): string {
  try {
    if (typeof value === "string") return value;
    if (value instanceof Error) return `${value.name}: ${value.message}`;
    return String(value);
  } catch {
    return "[unserializable]";
  }
}

function pushTail(level: "error" | "warn", args: unknown[]): void {
  const line = `[${level}] ${args.map(safeStringify).join(" ")}`.slice(
    0,
    TAIL_MAX_LINE_CHARS,
  );
  tail.push(line);
  while (tail.length > TAIL_MAX_LINES) tail.shift();
}

/** The current console tail, newest last, joined by newlines. */
export function consoleTail(): string {
  return tail.join("\n");
}

// ---------------------------------------------------------------------------
// Reporting gate + dedup.
// ---------------------------------------------------------------------------

/**
 * No-login pages: the address after the prefix is the link's secret, so
 * nothing about the page may leave the browser in a report (SL28).
 */
const NO_LOGIN_PREFIXES = ["/l/", "/f/"];

/**
 * Pre-auth routes drop their errors (ADR-0121 v1 — no session/tenant, so
 * no tenant-scoped row to write). Authenticated cockpit pages always live
 * under `/$tenantSlug/*`; `/auth/*` and the bare `/` redirect are pre-auth,
 * and the no-login pages never have a session at all.
 */
function isPreAuthRoute(pathname: string): boolean {
  return (
    pathname === "" ||
    pathname === "/" ||
    pathname.startsWith("/auth/") ||
    NO_LOGIN_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  );
}

/** Client-side dedup key: route + message with digit runs collapsed. */
function hashKey(title: string, route: string): string {
  const normMsg = title.replace(/\d+/g, "#").slice(0, NORM_MSG_MAX_CHARS);
  return `${route}|${normMsg}`;
}

interface ReportInput {
  title: string;
  stack: string;
  /** The originating Error, when available — marked in `reportedErrors`. */
  error?: unknown;
}

/**
 * File one auto report. Silent and non-amplifying: the whole body is
 * wrapped so a throw here never propagates or re-enters capture, the POST
 * is fire-and-forget, and dedup guarantees at most one report per hash per
 * session. Exported so the module error boundary reuses the exact pipeline.
 */
export function reportError(input: ReportInput): void {
  try {
    if (typeof window === "undefined") return;

    // Mark the Error before any early-return so the window listener and the
    // boundary can never both file it.
    if (input.error && typeof input.error === "object") {
      reportedErrors.add(input.error as object);
    }

    const route = window.location.pathname;
    if (isPreAuthRoute(route)) return;

    const key = hashKey(input.title, route);
    if (seenHashes.has(key)) return;
    seenHashes.add(key);

    const urlParams =
      typeof window.location.search === "string"
        ? window.location.search.replace(/^\?/, "")
        : "";
    const userAgent =
      typeof navigator !== "undefined" && navigator.userAgent
        ? navigator.userAgent
        : "";

    const pending = createIssue({
      kind: "auto",
      issue_type: "crash",
      severity: "med",
      title: input.title.slice(0, TITLE_MAX_CHARS),
      stack: input.stack,
      route,
      url_params: urlParams,
      user_agent: userAgent,
      console_tail: consoleTail(),
    });
    // Defensive: the real createIssue already swallows failures, but if a
    // test (or future refactor) hands back a rejecting promise, swallow it
    // here too so it can never surface as an unhandledrejection and loop.
    if (pending && typeof (pending as Promise<unknown>).catch === "function") {
      void (pending as Promise<unknown>).catch(() => undefined);
    }
  } catch {
    // The reporter must never throw into the app or re-enter capture.
  }
}

// ---------------------------------------------------------------------------
// Install.
// ---------------------------------------------------------------------------

export function installErrorCapture(): void {
  try {
    if (installed || typeof window === "undefined") return;
    installed = true;

    const origError = console.error.bind(console);
    console.error = (...args: unknown[]): void => {
      try {
        pushTail("error", args);
      } catch {
        // Ring-buffer failure must never break the passthrough below.
      }
      origError(...args);
    };

    const origWarn = console.warn.bind(console);
    console.warn = (...args: unknown[]): void => {
      try {
        pushTail("warn", args);
      } catch {
        // Ring-buffer failure must never break the passthrough below.
      }
      origWarn(...args);
    };

    window.addEventListener("error", (event: ErrorEvent) => {
      const err = event.error as unknown;
      if (err && typeof err === "object" && reportedErrors.has(err as object)) {
        return;
      }
      const stack =
        err && typeof err === "object" && typeof (err as Error).stack === "string"
          ? (err as Error).stack ?? ""
          : `${event.filename ?? ""}:${event.lineno ?? 0}:${event.colno ?? 0}`;
      reportError({
        title: event.message || "window error",
        stack,
        error: err,
      });
    });

    window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
      const reason = event.reason as unknown;
      const err =
        reason && typeof reason === "object" ? (reason as Error) : undefined;
      if (err && reportedErrors.has(err as object)) return;
      reportError({
        title: (err && err.message) || "unhandled rejection",
        stack: (err && err.stack) || safeStringify(reason),
        error: err,
      });
    });
  } catch {
    // Telemetry install must never break app boot.
  }
}
